import { useMemo, useRef } from "react";
import { AppEventPublisher } from "src/AppEventPublisher";
import { useEntityImageGeneration, type EntityImageGenerationAdapter, type UseEntityImageGenerationResult } from "../../../EntityImage/lib/useEntityImageGeneration";
import { generateWorldbuildingImage, listWorldbuildingImageStylePresets, summarizeWorldbuildingVisuals, worldbuildingImageApi, type WorldbuildingSummarizeResponse } from "./api";

export interface WorldbuildingBatchParams {
  aspectRatio: string | null;
}

const NO_STYLE_PRESET_ID = "none";
const DEFAULT_STYLE_IDS = ["concept-art-environment"];

const COPY = {
  allFailed: "Couldn't generate any images. Please try again.",
  allFailedWithReference: "Couldn't generate any images using that reference photo. Try a different image, or remove the reference image and generate again.",
  someFailedWithReference: "Some styles couldn't be generated using that reference photo. Retry them, or try a different reference image.",
  unsavedTiles: "One or more images generated but weren't saved — retry them before generating again or closing, or they'll be lost.",
};

// Both endpoints read the entry from the database, so any pending autosave (a type change, a
// just-typed field) has to land first.
function createWorldbuildingImageAdapter(
  entryId: string,
  flushPendingSave: () => Promise<unknown>,
): EntityImageGenerationAdapter<WorldbuildingSummarizeResponse, WorldbuildingBatchParams> {
  return {
    listStylePresets: async () => {
      await flushPendingSave();
      const response = await listWorldbuildingImageStylePresets(entryId);
      return { stylePresets: response.stylePresets, defaultStyleIds: [response.defaultStylePresetId], defaultBatchParams: { aspectRatio: response.defaultAspectRatio } };
    },
    summarize: async ({ description }) => {
      await flushPendingSave();
      return summarizeWorldbuildingVisuals(entryId, description);
    },
    generate: (summary, request, batchParams) =>
      generateWorldbuildingImage(entryId, {
        subject: summary.subject,
        mood: summary.mood,
        stylePresetId: request.stylePresetId,
        aspectRatio: batchParams?.aspectRatio ?? undefined,
        referenceImage: request.referenceImage,
        referenceImageGuidance: request.referenceImageGuidance,
        userDescription: request.userDescription,
        batchId: request.batchId,
      }),
    saveGenerated: (payload) => worldbuildingImageApi.saveGenerated(entryId, payload),
    upload: (payload) => worldbuildingImageApi.upload(entryId, payload),
    analytics: {
      generateClicked: (props, batchParams) =>
        void AppEventPublisher.worldbuildingImage.generateClicked({ worldbuildingEntryId: entryId, ...props, aspectRatio: batchParams?.aspectRatio ?? null }),
      tileResult: (props) => void AppEventPublisher.worldbuildingImage.tileResult({ worldbuildingEntryId: entryId, ...props }),
      retryTileClicked: (props) => void AppEventPublisher.worldbuildingImage.retryTileClicked({ worldbuildingEntryId: entryId, ...props }),
      saveBatchCompleted: (props) => void AppEventPublisher.worldbuildingImage.saveBatchCompleted({ worldbuildingEntryId: entryId, ...props }),
    },
    sentryFeaturePrefix: "worldbuilding_image",
    freeformStyle: { id: NO_STYLE_PRESET_ID, label: "Image" },
    defaultStyleIds: DEFAULT_STYLE_IDS,
    maxSelectedStyles: 4,
    copy: COPY,
  };
}

export interface UseWorldbuildingImageGenerationResult extends UseEntityImageGenerationResult<WorldbuildingBatchParams> {
  /** The ratio the next batch will use, from the entry's type; null (server resolves it) until presets load. */
  aspectRatio: string | null;
}

type EntityImageGenerationOptions = NonNullable<Parameters<typeof useEntityImageGeneration>[1]>;

export function useWorldbuildingImageGeneration(
  entryId: string,
  {
    role,
    flushPendingSave,
    ...options
  }: Omit<EntityImageGenerationOptions, "stylePresetsKey"> & {
    /** The entry's type; the offered styles and aspect ratio depend on it. */
    role?: string | null;
    flushPendingSave?: () => Promise<unknown> | undefined;
  } = {},
): UseWorldbuildingImageGenerationResult {
  const flushPendingSaveRef = useRef(flushPendingSave);
  flushPendingSaveRef.current = flushPendingSave;
  const adapter = useMemo(() => createWorldbuildingImageAdapter(entryId, async () => flushPendingSaveRef.current?.()), [entryId]);
  const result = useEntityImageGeneration(adapter, { ...options, stylePresetsKey: role ?? "" });
  return { ...result, aspectRatio: result.defaultBatchParams?.aspectRatio ?? null };
}
